/**
 * DataTable - IBKD规范数据表格组件
 */
import { useRef, useEffect, type CSSProperties, type ReactNode } from 'react'
import { clsx } from 'clsx'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from './Button'

export interface Column<T> {
  key: string | number
  title?: string
  header?: string
  /** 自定义表头内容，优先于 title/header */
  headerRender?: React.ReactNode
  width?: string | number
  minWidth?: string | number
  /**
   * 横向滚动时冻结列：left/right 为常见别名；start/end 与知情宽表一致，可配合 stickyAppearance。
   */
  sticky?: 'left' | 'right' | 'start' | 'end'
  /** start 列默认 flat、end 默认 elevated，避免左贴列「分层」感 */
  stickyAppearance?: 'flat' | 'elevated'
  align?: 'left' | 'center' | 'right'
  /** 表头文字对齐；未设置时与 align 一致 */
  headerAlign?: 'left' | 'center' | 'right'
  /** 追加到表头 th（如压缩前几列与下一列的视觉间距） */
  headerClassName?: string
  /** 追加到数据 td（桌面端表格） */
  cellClassName?: string
  /**
   * 单元格内容垂直对齐（表头与数据列一致用 middle；多行大块内容如「签署进度」可用 top；
   * 与 top 同列搭配时，操作列可用 bottom 使按钮与左侧大块内容底部对齐）
   */
  cellVAlign?: 'top' | 'middle' | 'bottom'
  /**
   * 自定义渲染。实现用 `render.length <= 1` 区分整行 `(record) =>` 与单元格 `(value, record, index) =>`。
   * 单参数 `(value) =>` 的 length 也是 1，会被误判为整行 record，列里会出现 `[object Object]`。
   * 按单元格取值时请写至少两形参：`(value, record) =>` 或 `(value, record, index) =>`。
   */
  render?: ((value: unknown, record: T, index: number) => React.ReactNode) | ((record: T) => React.ReactNode)
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  loading?: boolean
  emptyText?: string
  rowKey?: string | ((record: T) => string)
  /** 自定义行 className（如新建高亮） */
  rowClassName?: (record: T, index: number) => string | undefined
  onRowClick?: (record: T) => void
  currentPage?: number
  page?: number
  pageSize?: number
  total?: number
  onPageChange?: (page: number) => void
  pagination?: {
    current: number
    pageSize: number
    total: number
    onChange: (page: number) => void
  }
  /**
   * 桌面端将 thead / tbody 拆成两个同源 table（colgroup 对齐），并同步横向滚动。
   * 传入的回调用于把「仅表头」表格与页面标题、筛选等放在一起；与表体之间用 flex 分区，表体区域单独 overflow，避免整页滚动 + sticky 时表行叠在标题之上。
   * 头部块使用 max-h（视口比例）+ 内部纵向滚动，避免标题/筛选/卡片区过高时把表体 flex 挤成 0；表体设 min-h 保底可见行数。
   */
  renderDesktopStickyCluster?: (headerTable: ReactNode) => ReactNode
  /**
   * 紧凑模式：减小单元格左右内边距，便于宽表在小屏一屏内展示（仍依赖列宽总和与横向滚动容器）。
   */
  density?: 'default' | 'compact'
  /**
   * 桌面端 sticky 布局下，表体横向滚动容器的外侧负边距与内边距（需与 renderDesktopStickyCluster 内层与分页条对齐）。
   * 默认略宽；知情管理等宽表可传 `-mx-2 px-2 md:-mx-5 md:px-5` 让出横向像素。
   */
  desktopStickyScrollGutterClassName?: string
  /**
   * 为 true 时表体区域不占满父级剩余高度（去掉 flex-1），表格与下方分页条之间不留大块空白。
   * 行数较少、外侧自带分页条时使用。
   */
  desktopBodyShrinkToContent?: boolean
  /**
   * 与 `renderDesktopStickyCluster` 联用：表头簇 + 表体随内容增高，纵向由外层页面（如工作台 main 的 overflow-auto）滚动，
   * 避免表体在固定高度内单独出现纵向滚动条（放大字体时易裁切当前页行）。
   * 表体仍保留横向滚动以适配宽表；表头簇不再使用 max-h+内部纵向滚动。
   */
  desktopStickyClusterPageScroll?: boolean
  /**
   * 变化时（如翻页）将桌面端表体纵向滚动条重置到顶部，避免仍停留在上一页的中间位置。
   */
  scrollResetKey?: string | number
  /**
   * 为 false 时不使用「窄屏卡片行」布局，始终使用桌面表格 + 横向滚动（适合列很多的知情列表等）。
   * 默认 true 保持原有 md 以下卡片、md+ 表格行为。
   */
  stackedRowLayout?: boolean
  /**
   * 桌面端 table 最小宽度（px）。列宽总和较大时应设置，避免 table-fixed + w-full 把列压扁；不足宽度时出现横向滚动条。
   */
  desktopTableMinWidth?: number
}

export function DataTable<T extends object>({
  columns,
  data,
  loading = false,
  emptyText = '暂无数据',
  rowKey = 'id',
  rowClassName,
  onRowClick,
  currentPage,
  page,
  pageSize,
  total,
  onPageChange,
  pagination,
  renderDesktopStickyCluster,
  density = 'default',
  desktopStickyScrollGutterClassName = '-mx-3 px-3 md:-mx-6 md:px-6',
  desktopBodyShrinkToContent = false,
  desktopStickyClusterPageScroll = false,
  scrollResetKey,
  stackedRowLayout = true,
  desktopTableMinWidth,
}: DataTableProps<T>) {
  const stickyPageScroll = Boolean(renderDesktopStickyCluster && desktopStickyClusterPageScroll)
  const shrinkDesktopBody = desktopBodyShrinkToContent || stickyPageScroll
  const compact = density === 'compact'
  const hasStickyColumn = columns.some(
    (c) =>
      c.sticky === 'left' ||
      c.sticky === 'right' ||
      c.sticky === 'start' ||
      c.sticky === 'end',
  )
  const desktopHeadScrollRef = useRef<HTMLDivElement>(null)
  const desktopBodyScrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollResetKey === undefined) return
    const bodyEl = desktopBodyScrollRef.current
    if (bodyEl) bodyEl.scrollTop = 0
  }, [scrollResetKey])

  useEffect(() => {
    if (!renderDesktopStickyCluster) return
    const headEl = desktopHeadScrollRef.current
    const bodyEl = desktopBodyScrollRef.current
    if (!headEl || !bodyEl) return
    const syncHead = () => {
      bodyEl.scrollLeft = headEl.scrollLeft
    }
    const syncBody = () => {
      headEl.scrollLeft = bodyEl.scrollLeft
    }
    headEl.addEventListener('scroll', syncHead, { passive: true })
    bodyEl.addEventListener('scroll', syncBody, { passive: true })
    return () => {
      headEl.removeEventListener('scroll', syncHead)
      bodyEl.removeEventListener('scroll', syncBody)
    }
  }, [renderDesktopStickyCluster, columns, data, loading])
  const resolvedPagination = pagination ?? (
    typeof (currentPage ?? page) === 'number' &&
    typeof pageSize === 'number' &&
    typeof total === 'number' &&
    typeof onPageChange === 'function'
      ? { current: (currentPage ?? page) as number, pageSize, total, onChange: onPageChange }
      : undefined
  )

  const getRowKey = (record: T, index: number): string => {
    if (typeof rowKey === 'function') return rowKey(record)
    return String((record as Record<string, unknown>)[rowKey] ?? index)
  }

  const getValue = (record: T, key: string): unknown => {
    return key.split('.').reduce<unknown>(
      (obj, k) => (obj != null && typeof obj === 'object' ? (obj as Record<string, unknown>)[k] : undefined),
      record
    )
  }

  const totalPages = resolvedPagination
    ? Math.ceil(resolvedPagination.total / resolvedPagination.pageSize)
    : 0

  const columnWidthStyle = (col: Column<T>): CSSProperties | undefined => {
    const w = col.width
    const mw = col.minWidth
    if (w == null && mw == null) return undefined
    const style: CSSProperties = {}
    if (w != null) {
      style.width = w
      style.minWidth = mw ?? w
    } else if (mw != null) {
      style.minWidth = mw
    }
    return style
  }

  const stickyElevatedHeaderEnd =
    'sticky right-0 z-30 border-l border-slate-200/90 bg-slate-50 shadow-[-8px_0_14px_-6px_rgba(15,23,42,0.12)]'
  const stickyFlatHeaderStart =
    'sticky left-0 z-30 border-r border-slate-200 bg-slate-50'
  const stickyElevatedHeaderStart =
    'sticky left-0 z-30 border-r border-slate-200/90 bg-slate-50 shadow-[8px_0_14px_-6px_rgba(15,23,42,0.12)]'

  const stickyElevatedBodyEnd =
    'sticky right-0 z-20 border-l border-slate-200/85 bg-white/95 backdrop-blur-[1.5px] shadow-[-8px_0_14px_-6px_rgba(15,23,42,0.1)] group-hover:bg-slate-50/95'
  const stickyFlatBodyStart =
    'sticky left-0 z-20 border-r border-slate-200 bg-white group-hover:bg-slate-50'
  const stickyElevatedBodyStart =
    'sticky left-0 z-20 border-r border-slate-200/85 bg-white/95 backdrop-blur-[1.5px] shadow-[8px_0_14px_-6px_rgba(15,23,42,0.1)] group-hover:bg-slate-50/95'

  const stickyHeaderCombo = (col: Column<T>) => {
    if (col.sticky === 'end') return stickyElevatedHeaderEnd
    if (col.sticky === 'start') {
      return col.stickyAppearance === 'elevated' ? stickyElevatedHeaderStart : stickyFlatHeaderStart
    }
    if (col.sticky === 'left') {
      return 'sticky left-0 z-20 bg-slate-50 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)]'
    }
    if (col.sticky === 'right') {
      return 'sticky right-0 z-20 bg-slate-50 shadow-[-4px_0_8px_-4px_rgba(15,23,42,0.12)]'
    }
    return ''
  }

  const stickyBodyCombo = (col: Column<T>) => {
    if (col.sticky === 'end') return stickyElevatedBodyEnd
    if (col.sticky === 'start') {
      return col.stickyAppearance === 'elevated' ? stickyElevatedBodyStart : stickyFlatBodyStart
    }
    if (col.sticky === 'left') {
      return 'sticky left-0 z-10 bg-white shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)] group-hover:bg-slate-50'
    }
    if (col.sticky === 'right') {
      return 'sticky right-0 z-10 bg-white shadow-[-4px_0_8px_-4px_rgba(15,23,42,0.12)] group-hover:bg-slate-50'
    }
    return ''
  }

  /** 固定像素宽度，避免 table-fixed + w-full 在窄视口压扁列；滚动由外层 overflow-x 承担 */
  const desktopTableLayoutStyle =
    desktopTableMinWidth != null
      ? { width: desktopTableMinWidth, minWidth: desktopTableMinWidth }
      : undefined

  const headerTextAlign = (col: Column<T>) => col.headerAlign ?? col.align

  const headerCellClass = (col: Column<T>) =>
    clsx(
      compact ? 'px-2 py-2 text-xs font-semibold text-slate-700 whitespace-nowrap' : 'px-4 py-3 text-sm font-semibold text-slate-700 whitespace-nowrap',
      'border-b border-slate-200',
      col.cellVAlign === 'top' ? 'align-top' : 'align-middle',
      headerTextAlign(col) === 'center' && 'text-center',
      headerTextAlign(col) === 'right' && 'text-right',
      (!headerTextAlign(col) || headerTextAlign(col) === 'left') && 'text-left',
    )

  const bodyCellClass = (col: Column<T>) =>
    clsx(
      compact ? 'px-2 py-2 text-sm text-slate-600' : 'px-4 py-3 text-sm text-slate-600',
      'border-b border-slate-200',
      col.cellVAlign === 'top'
        ? 'align-top'
        : col.cellVAlign === 'bottom'
          ? 'align-bottom'
          : 'align-middle',
      col.align === 'center' && 'text-center',
      col.align === 'right' && 'text-right',
      (!col.align || col.align === 'left') && 'text-left',
    )

  const colgroup = (
    <colgroup>
      {columns.map((col) => (
        <col key={String(col.key)} style={columnWidthStyle(col)} />
      ))}
    </colgroup>
  )

  const theadRow = (
    <thead className="bg-slate-50">
      <tr>
        {columns.map((col) => (
          <th
            key={String(col.key)}
            data-column-key={String(col.key)}
            className={clsx(headerCellClass(col), stickyHeaderCombo(col), col.headerClassName)}
            style={columnWidthStyle(col)}
          >
            {col.headerRender ?? col.title ?? col.header ?? String(col.key)}
          </th>
        ))}
      </tr>
    </thead>
  )

  /** 行间分隔画在 td/th 上：border-separate + table-fixed 时 divide-y 对 tr 常不生效 */
  const tbodySection = (
    <tbody className="bg-white [&>tr:last-child>td]:border-b-0">
      {loading ? (
        <tr>
          <td colSpan={columns.length} className={compact ? 'px-2 py-6 text-center' : 'px-4 py-8 text-center'}>
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span>加载中...</span>
            </div>
          </td>
        </tr>
      ) : data.length === 0 ? (
        <tr>
          <td colSpan={columns.length} className={compact ? 'px-2 py-6 text-center text-slate-500' : 'px-4 py-8 text-center text-slate-500'}>
            {emptyText}
          </td>
        </tr>
      ) : (
        data.map((record, index) => (
          <tr
            key={getRowKey(record, index)}
            className={clsx(
              'group hover:bg-slate-50 transition-colors',
              onRowClick && 'cursor-pointer',
              rowClassName?.(record, index)
            )}
            onClick={onRowClick ? () => onRowClick(record) : undefined}
          >
            {columns.map((col) => (
              <td
                key={String(col.key)}
                data-column-key={String(col.key)}
                className={clsx(bodyCellClass(col), stickyBodyCombo(col), col.cellClassName)}
              >
                {col.render
                  ? ((col.render as any).length <= 1
                    ? (col.render as (record: T) => React.ReactNode)(record)
                    : (col.render as (value: unknown, record: T, index: number) => React.ReactNode)(
                      getValue(record, String(col.key)),
                      record,
                      index
                    ))
                  : String(getValue(record, String(col.key)) ?? '-')}
              </td>
            ))}
          </tr>
        ))
      )}
    </tbody>
  )

  const rootClass = clsx(
    'w-full min-w-0',
    renderDesktopStickyCluster &&
      (shrinkDesktopBody ? 'flex flex-col isolate' : 'flex min-h-0 min-w-0 flex-1 flex-col isolate'),
  )

  return (
    <div className={rootClass}>
      <div
        className={clsx(
          'cnkis-datatable-mobile-stack space-y-3',
          stackedRowLayout ? 'md:hidden' : 'hidden',
        )}
      >
        {loading ? (
          <div
            className={clsx(
              compact
                ? 'rounded-lg border border-slate-200 bg-white px-3 py-6 text-center text-sm text-slate-500'
                : 'rounded-lg border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500',
              'dark:border-[#3b434e] dark:bg-slate-800 dark:text-slate-400',
            )}
          >
            <div className="inline-flex items-center gap-2">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              <span>加载中...</span>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div
            className={clsx(
              compact
                ? 'rounded-lg border border-slate-200 bg-white px-3 py-6 text-center text-sm text-slate-500'
                : 'rounded-lg border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500',
              'dark:border-[#3b434e] dark:bg-slate-800 dark:text-slate-400',
            )}
          >
            {emptyText}
          </div>
        ) : (
          data.map((record, index) => (
            <div
              key={getRowKey(record, index)}
              className={clsx(
                compact
                  ? 'space-y-1.5 rounded-lg border border-slate-200 bg-white p-3'
                  : 'space-y-2 rounded-lg border border-slate-200 bg-white p-4',
                'dark:border-[#3b434e] dark:bg-slate-800',
                'hover:bg-slate-50 transition-colors dark:hover:bg-slate-700/35',
                onRowClick && 'cursor-pointer',
                rowClassName?.(record, index),
              )}
              onClick={onRowClick ? () => onRowClick(record) : undefined}
            >
              {columns.map((col) => {
                const title = col.title ?? col.header ?? String(col.key)
                const rendered = col.render
                  ? ((col.render as any).length <= 1
                    ? (col.render as (record: T) => React.ReactNode)(record)
                    : (col.render as (value: unknown, record: T, index: number) => React.ReactNode)(
                      getValue(record, String(col.key)),
                      record,
                      index
                    ))
                  : String(getValue(record, String(col.key)) ?? '-')
                const valueAlign =
                  col.align === 'center'
                    ? 'text-center'
                    : col.align === 'right'
                      ? 'text-right'
                      : 'text-left'
                return (
                  <div
                    key={String(col.key)}
                    className={clsx(
                      'gap-3',
                      col.align === 'center'
                        ? 'flex flex-col items-center text-center'
                        : 'flex items-start justify-between',
                    )}
                  >
                    <span className="shrink-0 text-xs font-medium text-slate-500 whitespace-nowrap dark:text-slate-400">
                      {title}
                    </span>
                    <div className={clsx('min-w-0 text-sm text-slate-700 dark:text-slate-200', valueAlign)}>{rendered}</div>
                  </div>
                )
              })}
            </div>
          ))
        )}
      </div>

      {renderDesktopStickyCluster ? (
        <div
          className={clsx(
            'min-w-0 flex flex-col overflow-hidden',
            stackedRowLayout ? 'hidden md:flex' : 'flex',
            shrinkDesktopBody ? '' : 'min-h-0 flex-1 md:min-h-0',
          )}
        >
          <div
            className={clsx(
              'relative z-10 min-w-0 shrink-0',
              stickyPageScroll
                ? 'min-h-0'
                : 'min-h-0 max-h-[65vh] overflow-y-auto overscroll-y-contain [scrollbar-width:thin]',
            )}
          >
            {renderDesktopStickyCluster(
              <div
                ref={desktopHeadScrollRef}
                className={clsx(
                  'min-w-0 overflow-x-auto overflow-y-hidden',
                  // 与表体互相同步 scrollLeft；仅表体展示横条，避免「表头下一条 + 表底一条」双横条
                  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
                )}
                style={{ WebkitOverflowScrolling: 'touch' } as CSSProperties}
              >
                <table
                  className={clsx(
                    'table-fixed border-separate border-spacing-0',
                    desktopTableMinWidth == null && 'w-full min-w-full',
                  )}
                  style={desktopTableLayoutStyle}
                >
                  {colgroup}
                  {theadRow}
                </table>
              </div>,
            )}
          </div>
          <div
            ref={desktopBodyScrollRef}
            className={clsx(
              'relative z-0 min-w-0 overscroll-x-contain',
              stickyPageScroll ? 'flex-none overflow-x-auto' : 'overflow-auto',
              shrinkDesktopBody ? 'flex-none' : 'min-h-[12rem] flex-1',
              desktopStickyScrollGutterClassName,
            )}
            style={{ WebkitOverflowScrolling: 'touch' } as CSSProperties}
          >
            <div className="min-w-0 rounded-b-xl border border-t-0 border-slate-200 bg-white dark:border-[#3b434e]">
              <table
                className={clsx(
                  'table-fixed border-separate border-spacing-0',
                  desktopTableMinWidth == null && 'w-full min-w-full',
                )}
                style={desktopTableLayoutStyle}
              >
                {colgroup}
                {tbodySection}
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={clsx(
            'cnkis-datatable-desktop-wrap min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-slate-200 dark:border-[#3b434e]',
            stackedRowLayout ? 'hidden md:block' : 'block',
          )}
        >
          <table
            className={clsx(
              'w-full',
              desktopTableMinWidth == null && 'min-w-full',
              hasStickyColumn && 'table-fixed border-separate border-spacing-0',
            )}
            style={desktopTableLayoutStyle}
          >
            {colgroup}
            {theadRow}
            {tbodySection}
          </table>
        </div>
      )}

      {/* 分页 */}
      {resolvedPagination && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between px-2">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            共 {resolvedPagination.total} 条
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={resolvedPagination.current <= 1}
              onClick={() => resolvedPagination.onChange(resolvedPagination.current - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {resolvedPagination.current} / {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={resolvedPagination.current >= totalPages}
              onClick={() => resolvedPagination.onChange(resolvedPagination.current + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

